/**
 * @zendir/ui - GlassCard Component
 *
 * Glassmorphic card component with transparent blur backgrounds,
 * gradient overlays, and Zendir brand color accent system.
 *
 * Inspired by AstroUXDS radar chart styling with modern glassmorphism.
 *
 * @example
 * ```tsx
 * <GlassCard title="Radar Chart 001" colorOverlay="electric">
 *   <RadarChart data={data} />
 * </GlassCard>
 *
 * <GlassCard title="System Status" colorOverlay="purple" accentPosition="top">
 *   <StatusContent />
 * </GlassCard>
 * ```
 */

import React, { memo } from "react";
import { useTheme } from "../../theme";
import { classNames, type StatusLevel } from "../../utils";

// =============================================================================
// Zendir Brand Color Palette for Glass Overlays
// =============================================================================

export const GLASS_COLOR_OVERLAYS = {
  // Core brand colors
  electric: {
    gradient:
      "linear-gradient(135deg, rgba(62, 60, 255, 0.15) 0%, rgba(62, 60, 255, 0.05) 100%)",
    accent: "#3E3CFF",
    border: "rgba(62, 60, 255, 0.3)",
    glow: "rgba(62, 60, 255, 0.4)",
  },
  purple: {
    gradient:
      "linear-gradient(135deg, rgba(157, 112, 255, 0.15) 0%, rgba(157, 112, 255, 0.05) 100%)",
    accent: "#9D70FF",
    border: "rgba(157, 112, 255, 0.3)",
    glow: "rgba(157, 112, 255, 0.4)",
  },
  prussianBlue: {
    gradient:
      "linear-gradient(135deg, rgba(27, 45, 160, 0.2) 0%, rgba(27, 45, 160, 0.08) 100%)",
    accent: "#1B2DA0",
    border: "rgba(27, 45, 160, 0.4)",
    glow: "rgba(27, 45, 160, 0.5)",
  },

  // Cyan/Teal (matching the reference image style)
  cyan: {
    gradient:
      "linear-gradient(135deg, rgba(0, 212, 255, 0.12) 0%, rgba(0, 180, 220, 0.04) 100%)",
    accent: "#00D4FF",
    border: "rgba(0, 212, 255, 0.35)",
    glow: "rgba(0, 212, 255, 0.5)",
  },
  teal: {
    gradient:
      "linear-gradient(135deg, rgba(45, 204, 255, 0.12) 0%, rgba(45, 204, 255, 0.04) 100%)",
    accent: "#2DCCFF",
    border: "rgba(45, 204, 255, 0.35)",
    glow: "rgba(45, 204, 255, 0.5)",
  },

  // Neutral options
  dark: {
    gradient:
      "linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 100%)",
    accent: "#1a1a2e",
    border: "rgba(255, 255, 255, 0.1)",
    glow: "rgba(0, 0, 0, 0.3)",
  },
  light: {
    gradient:
      "linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)",
    accent: "#ffffff",
    border: "rgba(255, 255, 255, 0.25)",
    glow: "rgba(255, 255, 255, 0.3)",
  },
  white: {
    gradient:
      "linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.08) 100%)",
    accent: "#ffffff",
    border: "rgba(255, 255, 255, 0.3)",
    glow: "rgba(255, 255, 255, 0.4)",
  },

  // Additional shades
  electricLight: {
    gradient:
      "linear-gradient(135deg, rgba(107, 105, 255, 0.15) 0%, rgba(107, 105, 255, 0.05) 100%)",
    accent: "#6B69FF",
    border: "rgba(107, 105, 255, 0.3)",
    glow: "rgba(107, 105, 255, 0.4)",
  },
  purpleLight: {
    gradient:
      "linear-gradient(135deg, rgba(180, 141, 255, 0.15) 0%, rgba(180, 141, 255, 0.05) 100%)",
    accent: "#B48DFF",
    border: "rgba(180, 141, 255, 0.3)",
    glow: "rgba(180, 141, 255, 0.4)",
  },

  // No overlay (transparent)
  none: {
    gradient: "none",
    accent: "transparent",
    border: "rgba(255, 255, 255, 0.1)",
    glow: "transparent",
  },
} as const;

export type GlassColorOverlay = keyof typeof GLASS_COLOR_OVERLAYS;

export type GlassAccentPosition = "top" | "left" | "right" | "bottom" | "none";

export interface GlassCardProps {
  /** Card title with optional icon */
  title?: React.ReactNode;
  /** Subtitle text */
  subtitle?: React.ReactNode;
  /** Color overlay from Zendir brand palette */
  colorOverlay?: GlassColorOverlay;
  /** Custom color overlay (hex or rgba) */
  customOverlay?: {
    gradient: string;
    accent: string;
    border: string;
    glow?: string;
  };
  /**
   * Accent color (hex) - auto-generates overlay from this color
   * Overrides colorOverlay if provided
   * Can be used with CardAccentProvider: accentColor={getAccentColor(title)}
   */
  accentColor?: string;
  /** Position of the accent line/glow */
  accentPosition?: GlassAccentPosition;
  /** Accent line style */
  accentStyle?: "solid" | "gradient" | "glow" | "dots";
  /** Header icon (rendered before title) */
  icon?: React.ReactNode;
  /** Header right actions (legend, buttons, etc.) */
  actions?: React.ReactNode;
  /** Legend items to display in header */
  legend?: Array<{ label: string; color: string }>;
  /** Status indicator */
  status?: StatusLevel;
  /** Blur intensity (0-20) */
  blur?: number;
  /** Background opacity (0-1) */
  backgroundOpacity?: number;
  /** Enable glow effect on hover */
  glowOnHover?: boolean;
  /** Children content */
  children: React.ReactNode;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
  /** Click handler */
  onClick?: () => void;
  /** Full height mode */
  fullHeight?: boolean;
  /** Padding size */
  padding?: "none" | "sm" | "md" | "lg";
}

/**
 * GlassCard - Glassmorphic card with Zendir brand styling
 *
 * Features:
 * - Transparent blur background
 * - Gradient color overlays
 * - Accent lines with glow effects
 * - Legend support for charts
 * - Customizable via props or custom overlay object
 */
/**
 * Create overlay from accent color hex
 */
function createOverlayFromColor(color: string) {
  // Extract RGB from hex
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return {
    gradient: `linear-gradient(135deg, rgba(${r}, ${g}, ${b}, 0.15) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%)`,
    accent: color,
    border: `rgba(${r}, ${g}, ${b}, 0.35)`,
    glow: `rgba(${r}, ${g}, ${b}, 0.5)`,
  };
}

export const GlassCard = memo(function GlassCard({
  title,
  subtitle,
  colorOverlay = "cyan",
  customOverlay,
  accentColor,
  accentPosition = "top",
  accentStyle = "gradient",
  icon,
  actions,
  legend,
  status,
  blur = 12,
  backgroundOpacity = 0.85,
  glowOnHover = true,
  children,
  className = "",
  style,
  onClick,
  fullHeight = false,
  padding = "md",
}: GlassCardProps): React.ReactElement {
  const { tokens, mode, theme } = useTheme();
  const isDark = mode === "dark";
  const isTransparent =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const isBoldVariant = theme === "transparent-bold";
  const isMinimalVariant = theme === "transparent-minimal";

  // Accent styling only for Bold/Minimal themes (or when position explicitly set for non-transparent)
  const showAccentStyling = isBoldVariant || isMinimalVariant || !isTransparent;

  // For all transparent themes, default to purple (Zendir accent) instead of cyan
  const effectiveColorOverlay =
    isTransparent && colorOverlay === "cyan" ? "purple" : colorOverlay;

  // Get overlay colors (priority: accentColor > customOverlay > colorOverlay)
  const overlay = accentColor
    ? createOverlayFromColor(accentColor)
    : customOverlay || GLASS_COLOR_OVERLAYS[effectiveColorOverlay];

  // Auto-switch to light token for 'white' overlay in light mode
  const effectiveOverlay =
    mode === "light" &&
    colorOverlay === "white" &&
    !accentColor &&
    !customOverlay
      ? GLASS_COLOR_OVERLAYS.dark
      : overlay;

  // Theme-aware background colors
  const cardBackground = isDark
    ? `rgba(15, 20, 35, ${backgroundOpacity})`
    : `rgba(255, 255, 255, ${backgroundOpacity})`;

  // Theme-aware border colors
  const baseBorderColor = isDark
    ? effectiveOverlay.border
    : `rgba(100, 116, 139, 0.25)`;

  const paddingConfig = {
    none: "0",
    sm: tokens.spacing.sm,
    md: tokens.spacing.md,
    lg: tokens.spacing.lg,
  };

  const isInteractive = !!onClick;

  // Accent line styles based on position and style
  // Only show accent styling for Bold/Minimal themes (or non-transparent themes)
  const getAccentStyles = (): React.CSSProperties => {
    // For normal Glass theme, use subtle muted border instead of colored accent
    if (isTransparent && !showAccentStyling) {
      return {};
    }

    if (accentPosition === "none") return {};

    const accentColorValue = effectiveOverlay.accent;
    const glowColor = effectiveOverlay.glow || accentColorValue;

    const baseAccentStyle: React.CSSProperties = {};

    // Position-based border
    const positions: Record<GlassAccentPosition, string> = {
      top: "borderTop",
      left: "borderLeft",
      right: "borderRight",
      bottom: "borderBottom",
      none: "",
    };

    const borderProp = positions[accentPosition] as keyof React.CSSProperties;

    if (accentStyle === "solid") {
      (baseAccentStyle as any)[borderProp] = `2px solid ${accentColorValue}`;
    } else if (accentStyle === "gradient" || accentStyle === "glow") {
      // Using pseudo-element for gradient, so just set position indicator
      (baseAccentStyle as any)[borderProp] = `2px solid ${accentColorValue}`;
      if (accentStyle === "glow") {
        baseAccentStyle.boxShadow = `0 -2px 20px ${glowColor}, 0 -1px 8px ${glowColor}`;
      }
    } else if (accentStyle === "dots") {
      (baseAccentStyle as any)[borderProp] = `2px dotted ${accentColorValue}`;
    }

    return baseAccentStyle;
  };

  // Status color
  const statusColor = status ? tokens.colors.status[status] : undefined;

  return (
    <div
      className={classNames(
        "zendir-glass-card",
        `zendir-glass-card--${colorOverlay}`,
        className,
      )}
      onClick={onClick}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      style={{
        position: "relative",
        borderRadius: tokens.borderRadius.lg,
        overflow: "hidden",
        height: fullHeight ? "100%" : "auto",
        display: "flex",
        flexDirection: "column",
        cursor: isInteractive ? "pointer" : "default",

        // Glassmorphic base (theme-aware)
        background: cardBackground,
        backdropFilter: isTransparent ? `blur(${blur}px)` : "none",
        WebkitBackdropFilter: isTransparent ? `blur(${blur}px)` : "none",

        // Border with color tint (theme-aware)
        border: `1px solid ${baseBorderColor}`,

        // Accent positioning
        ...getAccentStyles(),

        // Hover glow effect
        transition: "all 0.3s ease-out",
        boxShadow: glowOnHover ? "none" : undefined,

        ...style,
      }}
      onMouseEnter={(e) => {
        if (glowOnHover && effectiveOverlay.glow) {
          e.currentTarget.style.boxShadow = `0 0 30px ${effectiveOverlay.glow}, 0 4px 20px rgba(0,0,0,0.3)`;
        }
      }}
      onMouseLeave={(e) => {
        if (glowOnHover) {
          e.currentTarget.style.boxShadow = "none";
        }
      }}
    >
      {/* Gradient overlay layer */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: effectiveOverlay.gradient,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Accent glow line (top) - only for Bold/Minimal themes */}
      {showAccentStyling &&
        accentPosition === "top" &&
        accentStyle === "glow" && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: "10%",
              right: "10%",
              height: "2px",
              background: `linear-gradient(90deg, transparent, ${effectiveOverlay.accent}, transparent)`,
              boxShadow: `0 0 15px ${effectiveOverlay.glow}, 0 0 30px ${effectiveOverlay.glow}`,
              zIndex: 1,
            }}
          />
        )}

      {/* Content wrapper */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        {/* Header */}
        {(title || actions || legend) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: tokens.spacing.md,
              padding: `${paddingConfig[padding]} ${paddingConfig[padding]} 0`,
              marginBottom: tokens.spacing.sm,
            }}
          >
            {/* Title section */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.spacing.sm,
              }}
            >
              {/* Icon */}
              {icon && (
                <span
                  style={{
                    color: effectiveOverlay.accent,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {icon}
                </span>
              )}

              {/* Status shape — Astro UX dual-coded */}
              {status &&
                statusColor &&
                (() => {
                  const s = 10;
                  const h = s / 2;
                  const glow = `drop-shadow(0 0 4px ${statusColor})`;
                  switch (status) {
                    case "critical":
                      return (
                        <svg
                          width={s}
                          height={s}
                          viewBox={`0 0 ${s} ${s}`}
                          style={{ flexShrink: 0, filter: glow }}
                        >
                          <polygon
                            points={`${h},${s} 0,0 ${s},0`}
                            fill={statusColor}
                          />
                        </svg>
                      );
                    case "serious":
                      return (
                        <svg
                          width={s}
                          height={s}
                          viewBox={`0 0 ${s} ${s}`}
                          style={{ flexShrink: 0, filter: glow }}
                        >
                          <polygon
                            points={`${h},0 ${s},${h} ${h},${s} 0,${h}`}
                            fill={statusColor}
                          />
                        </svg>
                      );
                    case "caution":
                      return (
                        <svg
                          width={s}
                          height={s}
                          viewBox={`0 0 ${s} ${s}`}
                          style={{ flexShrink: 0, filter: glow }}
                        >
                          <rect width={s} height={s} fill={statusColor} />
                        </svg>
                      );
                    case "standby":
                      return (
                        <svg
                          width={s}
                          height={s}
                          viewBox="0 0 12 12"
                          style={{ flexShrink: 0, filter: glow }}
                        >
                          <circle
                            cx="6"
                            cy="6"
                            r="3.5"
                            fill="none"
                            stroke={statusColor}
                            strokeWidth="2"
                          />
                        </svg>
                      );
                    case "off":
                      return (
                        <svg
                          width={s}
                          height={s}
                          viewBox="0 0 12 12"
                          style={{ flexShrink: 0, filter: glow }}
                        >
                          <circle cx="6" cy="6" r="3" fill={statusColor} />
                        </svg>
                      );
                    default: // normal
                      return (
                        <svg
                          width={s}
                          height={s}
                          viewBox={`0 0 ${s} ${s}`}
                          style={{ flexShrink: 0, filter: glow }}
                        >
                          <circle cx={h} cy={h} r={h} fill={statusColor} />
                        </svg>
                      );
                  }
                })()}

              {/* Title text — card heading system */}
              <div>
                {title && (
                  <h3
                    style={{
                      margin: 0,
                      fontSize:
                        tokens.layout?.card?.heading?.titleFontSize ??
                        "1.125rem",
                      fontWeight:
                        tokens.layout?.card?.heading?.titleFontWeight ?? 500,
                      color: effectiveOverlay.accent,
                      letterSpacing: "0.5px",
                      textShadow: `0 0 20px ${effectiveOverlay.glow}`,
                    }}
                  >
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <p
                    style={{
                      margin: `${tokens.spacing.xs} 0 0`,
                      fontSize: tokens.typography.body[3].fontSize,
                      color: tokens.colors.text.secondary,
                    }}
                  >
                    {subtitle}
                  </p>
                )}
              </div>
            </div>

            {/* Right section: legend + actions */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.spacing.md,
              }}
            >
              {/* Legend items */}
              {legend && legend.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: tokens.spacing.md,
                  }}
                >
                  {legend.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: tokens.spacing.xs,
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "2px",
                          backgroundColor: item.color,
                        }}
                      />
                      <span
                        style={{
                          fontSize: tokens.typography.body[3].fontSize,
                          color: tokens.colors.text.secondary,
                        }}
                      >
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              {actions && (
                <div style={{ display: "flex", gap: tokens.spacing.xs }}>
                  {actions}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Accent line - only for Bold/Minimal themes (no decorative dashes) */}
        {showAccentStyling && accentPosition === "top" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: `0 ${paddingConfig[padding]}`,
              marginBottom: tokens.spacing.sm,
            }}
          >
            {/* Clean accent line with smooth gradient */}
            <div
              style={{
                flex: 1,
                height: "2px",
                background: `linear-gradient(90deg, ${effectiveOverlay.accent} 0%, ${effectiveOverlay.accent}60 70%, ${effectiveOverlay.accent}20 100%)`,
                boxShadow: `0 0 6px ${effectiveOverlay.accent}40`,
              }}
            />
          </div>
        )}

        {/* Content */}
        <div
          style={{
            flex: 1,
            padding: title
              ? `0 ${paddingConfig[padding]} ${paddingConfig[padding]}`
              : paddingConfig[padding],
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
});

export default GlassCard;
